import dayjs from 'dayjs';

import type {
  MarketingRouterExperimentDecision,
  MarketingRouterMetricResult,
} from '@luxuryescapes/contract-svc-promo/marketing-router/admin';

/**
 * Pure display mapping for the marketing router experiment detail page.
 * The svc-promo significance endpoint deliberately returns codes and raw
 * statistics with no copy — every marketer-facing wording lives here so it
 * can be unit tested without rendering.
 */

export type MarketingRouterMetricKey = MarketingRouterMetricResult['metric'];

export const METRIC_DISPLAY: Record<
  MarketingRouterMetricKey,
  { name: string; perSessionLabel: string; totalLabel: string }
> = {
  purchase: { name: 'Purchase rate', perSessionLabel: 'Purchase rate', totalLabel: 'Purchases' },
  ttv: { name: 'TTV', perSessionLabel: 'TTV per session', totalLabel: 'Total TTV' },
  margin: { name: 'Margin', perSessionLabel: 'Margin per session', totalLabel: 'Total margin' },
};

const audFormatter = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });
const countFormatter = new Intl.NumberFormat('en-AU', { maximumFractionDigits: 0 });

export function formatMetricValue(metric: MarketingRouterMetricKey, value: number): string {
  if (metric === 'purchase') {
    return `${(value * 100).toFixed(2)}%`;
  }
  return audFormatter.format(value);
}

export function formatMetricTotal(metric: MarketingRouterMetricKey, value: number): string {
  if (metric === 'purchase') {
    return countFormatter.format(Math.round(value));
  }
  return audFormatter.format(value);
}

export function formatSessions(value: number): string {
  return countFormatter.format(value);
}

export function formatRelativeLift(relativeLift: number | null): string {
  if (relativeLift === null || !Number.isFinite(relativeLift)) {
    return '—';
  }
  const percent = relativeLift * 100;
  const sign = percent > 0 ? '+' : '';
  return `${sign}${percent.toFixed(1)}%`;
}

/**
 * Collapses the relative 95% CI into a single "give or take" number: half the
 * interval width, as a percentage. Null when the backend could not compute a
 * relative interval (zero base value).
 */
export function getLiftMarginOfErrorPercent(relativeCi95: number[] | null): number | null {
  if (!relativeCi95 || relativeCi95.length !== 2) {
    return null;
  }
  const [lower, upper] = relativeCi95;
  if (!Number.isFinite(lower) || !Number.isFinite(upper)) {
    return null;
  }
  return ((upper - lower) / 2) * 100;
}

/** Optimizely-style single confidence number: (1 - p) as a display percent. */
export function formatConfidence(result: MarketingRouterMetricResult): string {
  if (result.verdict === 'COLLECTING') {
    return '—';
  }
  const confidence = (1 - result.pAdjusted) * 100;
  if (confidence >= 99) {
    return '>99%';
  }
  if (confidence <= 1) {
    return '<1%';
  }
  return `${confidence.toFixed(0)}%`;
}

export type RuleRunStatus = {
  label: 'Scheduled' | 'Running' | 'Ended';
  color: 'default' | 'success' | 'info';
};

export function getRuleRunStatus(startDate: string, endDate: string, now: dayjs.Dayjs): RuleRunStatus {
  if (now.isBefore(dayjs(startDate), 'day')) {
    return { label: 'Scheduled', color: 'default' };
  }
  if (now.isAfter(dayjs(endDate), 'day')) {
    return { label: 'Ended', color: 'info' };
  }
  return { label: 'Running', color: 'success' };
}

/**
 * Duration copy for the header tile: "Day 12 of 30" while running, with
 * plain-language variants either side of the window.
 */
export function formatDuration(startDate: string, endDate: string, now: dayjs.Dayjs): string {
  const start = dayjs(startDate).startOf('day');
  const end = dayjs(endDate).startOf('day');
  const today = now.startOf('day');
  // Both endpoints inclusive: a rule running 1st..30th is a 30-day experiment.
  const totalDays = Math.max(end.diff(start, 'day') + 1, 1);

  if (today.isBefore(start)) {
    const daysUntil = start.diff(today, 'day');
    return daysUntil === 1 ? 'Starts tomorrow' : `Starts in ${daysUntil} days`;
  }
  if (today.isAfter(end)) {
    return `Ran ${totalDays} ${totalDays === 1 ? 'day' : 'days'}`;
  }
  const dayNumber = Math.min(today.diff(start, 'day') + 1, totalDays);
  return `Day ${dayNumber} of ${totalDays}`;
}

export function formatTrafficAllocation(weights: number[]): string {
  return weights.map((weight) => `${weight}%`).join(' / ');
}

export type DecisionDisplay = {
  severity: 'success' | 'warning' | 'error' | 'info';
  title: string;
  description: string;
};

const numberOfSessions = (count: number): string => `${formatSessions(count)} sessions`;

/**
 * Maps a ship/no-ship decision code to marketer-facing copy. Kept free of
 * statistics jargon on purpose — the numbers backing it live in the metric
 * tables below the decision card.
 */
export function getDecisionDisplay(
  decision: MarketingRouterExperimentDecision,
  baseVariantName: string,
): DecisionDisplay {
  const metricName = METRIC_DISPLAY[decision.primaryMetric].name.toLowerCase();
  const lift = formatRelativeLift(decision.primaryRelativeLift);
  const degraded = decision.degradedSecondaryMetrics
    .map((metric) => METRIC_DISPLAY[metric].name.toLowerCase())
    .join(' and ');

  switch (decision.code) {
    case 'SHIP':
      return {
        severity: 'success',
        title: `Ship ${decision.variantName}`,
        description: `${decision.variantName} beat ${baseVariantName} on ${metricName} (${lift}) and nothing got significantly worse.`,
      };
    case 'SHIP_WITH_TRADEOFFS':
      return {
        severity: 'warning',
        title: `${decision.variantName} wins on ${metricName}, with trade-offs`,
        description: `${decision.variantName} improved ${metricName} by ${lift} vs ${baseVariantName}, but ${degraded} got significantly worse. Decide whether the trade-off is acceptable before shipping.`,
      };
    case 'DO_NOT_SHIP':
      return {
        severity: 'error',
        title: `Keep ${baseVariantName}`,
        description: `${decision.variantName} performed significantly worse than ${baseVariantName} on ${metricName} (${lift}). Do not ship it.`,
      };
    case 'COLLECTING':
      return {
        severity: 'info',
        title: 'Still collecting data',
        description:
          decision.remainingSessions !== undefined && decision.remainingSessions > 0
            ? `${decision.variantName} needs roughly ${numberOfSessions(decision.remainingSessions)} more before a reliable call can be made.`
            : `${decision.variantName} does not have enough sessions yet for a reliable call.`,
      };
    case 'INVALID_SRM':
      return {
        severity: 'error',
        title: 'Results cannot be trusted',
        description:
          'Traffic is not splitting between variants the way the rule is configured, so every number on this page is unreliable. Check the rule setup and targeting before reading anything into the results.',
      };
    case 'INCONCLUSIVE':
      return {
        severity: 'info',
        title: 'No clear winner yet',
        description: `The difference between ${decision.variantName} and ${baseVariantName} on ${metricName} (${lift}) is too small to be confident it is real. Keep the experiment running or call it a draw.`,
      };
  }
}

export type VariantSessionsSummary = {
  variantId: string;
  variantName: string;
  sessions: number;
  isControl: boolean;
};

/**
 * Per-variant analytical session counts, derived from the metric results
 * (baseN/treatN are constant across metrics for a given variant pair).
 * Control first, then treatments in result order.
 */
export function summariseVariantSessions(
  results: MarketingRouterMetricResult[],
  baseVariantId: string,
  baseVariantName: string,
): VariantSessionsSummary[] {
  const summaries: VariantSessionsSummary[] = [];
  if (results.length > 0) {
    summaries.push({
      variantId: baseVariantId,
      variantName: baseVariantName,
      sessions: results[0].baseN,
      isControl: true,
    });
  }
  const seenTreatmentIds = new Set<string>();
  for (const result of results) {
    if (seenTreatmentIds.has(result.variantId)) {
      continue;
    }
    seenTreatmentIds.add(result.variantId);
    summaries.push({
      variantId: result.variantId,
      variantName: result.variantName,
      sessions: result.treatN,
      isControl: false,
    });
  }
  return summaries;
}

export function totalSessions(summaries: VariantSessionsSummary[]): number {
  return summaries.reduce((total, summary) => total + summary.sessions, 0);
}
